import React, { useState } from 'react';
import {
  Loader2,
  AlertTriangle,
  Search,
  PlusCircle,
  Pencil,
  Plus,
} from 'lucide-react';
import {
  useBookCopies,
  useInventoryStatistics,
  useBulkStatusUpdate,
  type BookCopy
} from '@/hooks/inventory-hooks';
import { UpdateBookCopyForm, AddBookCopyForm } from '@/components/admin/components';
import { useDensity } from '@/components/admin/context/DensityContext';
import LibButton from '@/components/librarian/ui/LibButton';

const InventoryModule: React.FC = () => {
  const { density } = useDensity();

  // Fetch real inventory data using the new hooks
  const { data: bookCopiesData, isLoading: isLoadingCopies, error: copiesError, refetch: refetchCopies } = useBookCopies();
  const { data: inventoryStats, isLoading: isLoadingStats, refetch: refetchInventoryStats } = useInventoryStatistics();
  const bulkStatusUpdate = useBulkStatusUpdate();

  // State for update dialog
  const [selectedCopy, setSelectedCopy] = useState<BookCopy | null>(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);

  // State for add copies dialog
  const [selectedBookForAddCopies, setSelectedBookForAddCopies] = useState<{ id: number; title: string } | null>(null);
  const [isAddCopiesDialogOpen, setIsAddCopiesDialogOpen] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  const bookCopies = bookCopiesData?.results || [];

  // Group copies by book for display
  const bookGroups = bookCopies.reduce((acc, copy) => {
    const bookId = copy.book_info?.id || copy.book_id || -1;
    if (bookId === -1) return acc;
    if (!acc[bookId]) {
      acc[bookId] = {
        book_info: copy.book_info,
        copies: [],
        totalCopies: 0,
        availableCopies: 0,
        checkedOutCopies: 0,
      };
    }
    acc[bookId].copies.push(copy);
    acc[bookId].totalCopies++;
    if (copy.status === 'available') acc[bookId].availableCopies++;
    else if (copy.status === 'borrowed') acc[bookId].checkedOutCopies++;
    return acc;
  }, {} as Record<number, {
    book_info?: BookCopy['book_info'];
    copies: BookCopy[];
    totalCopies: number;
    availableCopies: number;
    checkedOutCopies: number;
  }>);

  const books = Object.values(bookGroups);

  // Calculate stats
  const totalCopies = inventoryStats?.total_copies || bookCopies.length;
  const availableCopies = inventoryStats?.available_copies || 0;
  const checkedOutCopies = inventoryStats?.status_breakdown?.find(item => item.status === 'borrowed')?.count || 0;
  const damagedCopies = inventoryStats?.maintenance_copies || 0;
  const missingCopies = inventoryStats?.status_breakdown?.find(item => item.status === 'missing')?.count || 0;

  // Handler functions
  const handleEditCopy = (book: typeof books[0]) => {
    const firstCopy = book.copies[0];
    if (firstCopy) {
      setSelectedCopy(firstCopy);
      setIsUpdateDialogOpen(true);
    }
  };

  const handleUpdateSuccess = () => {
    refetchCopies();
    setIsUpdateDialogOpen(false);
    setSelectedCopy(null);
  };

  const handleAddCopiesSuccess = () => {
    refetchCopies();
    refetchInventoryStats();
    setIsAddCopiesDialogOpen(false);
    setSelectedBookForAddCopies(null);
  };

  const handleAddCopies = (book: typeof books[0]) => {
    if (book.book_info) {
      setSelectedBookForAddCopies({
        id: book.book_info.id,
        title: book.book_info.title || 'Unknown Title'
      });
      setIsAddCopiesDialogOpen(true);
    }
  };

  const handleQuickStatusChange = async (
    book: typeof books[0],
    newStatus: BookCopy['status']
  ) => {
    const copyIds = book.copies.map(copy => copy.id);
    if (copyIds.length === 0) return;
    try {
      await bulkStatusUpdate.mutateAsync({
        copy_ids: copyIds,
        new_status: newStatus,
        notes: `Quick status update for ${book.book_info?.title ?? 'book'}`,
      });
      await Promise.allSettled([
        refetchCopies(),
        refetchInventoryStats(),
      ]);
    } catch (error) {
      console.error('Quick status update failed:', error);
    }
  };

  // Filter books by search query
  const filteredBooks = searchQuery
    ? books.filter(book =>
        book.book_info?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.book_info?.isbn?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : books;

  // Loading state
  if (isLoadingCopies || isLoadingStats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--lib-gold)]" />
          <span className="text-sm text-[var(--lib-ink-muted)]">Loading inventory data...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (copiesError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-[var(--lib-danger)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--lib-ink)] mb-2">Failed to Load Inventory</h3>
          <p className="text-sm text-[var(--lib-ink-muted)]">There was an error loading the inventory data. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)]">Inventory</h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">Monitor physical copies and stock.</p>
        </div>
        <LibButton size="none"
          onClick={() => setIsAddCopiesDialogOpen(true)} className="hover:bg-[var(--lib-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          Add Copy
        </LibButton>
      </div>

      {/* Stats row: 4 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Copies', value: totalCopies.toLocaleString() },
          { label: 'Available', value: availableCopies.toLocaleString() },
          { label: 'Checked Out', value: checkedOutCopies.toLocaleString() },
          { label: 'Damaged/Lost', value: (damagedCopies + missingCopies).toLocaleString() },
        ].map(s => (
          <div key={s.label} className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg px-5 py-4">
            <div className="font-display text-[22px] font-bold text-[var(--lib-ink)]">{s.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--lib-ink-muted)]" />
          <input
            type="search"
            placeholder="Search copies..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg text-sm text-[var(--lib-ink-muted)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <span className="text-xs text-[var(--lib-ink-muted)] font-mono ml-auto">{filteredBooks.length} records</span>
      </div>

      {/* Table */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[var(--lib-surface-2)] border-b border-[var(--lib-hairline)]">
            <tr>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Book Title</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">ISBN</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Total</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Available</th>
              <th className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">Status</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--lib-hairline)]">
            {filteredBooks.map((book, index) => {
              const availableCount = book.availableCopies;
              const statusConfig =
                availableCount === 0
                  ? { bg: 'bg-[var(--lib-danger)]/10', text: 'text-[var(--lib-danger)]', border: 'border-[var(--lib-danger)]', label: 'Out of Stock' }
                  : availableCount <= 2
                  ? { bg: 'bg-[var(--lib-gold)]/10', text: 'text-[var(--lib-gold-text)]', border: 'border-[var(--lib-gold)]', label: 'Low Stock' }
                  : { bg: 'bg-[var(--lib-success)]/10', text: 'text-[var(--lib-success)]', border: 'border-[var(--lib-success)]', label: 'In Stock' };

              return (
                <tr key={book.book_info?.id ?? index} className="hover:bg-[var(--lib-surface-2)] transition-colors group">
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-medium text-[var(--lib-ink)]`}>
                    <div>{book.book_info?.title || '—'}</div>
                    <div className="text-xs text-[var(--lib-ink-muted)] mt-0.5">
                      {book.book_info?.authors?.map(a => a.full_name).join(', ') || ''}
                    </div>
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-[var(--lib-ink-muted)] font-mono`}>
                    {book.book_info?.isbn || '—'}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-medium text-[var(--lib-ink)]`}>
                    {book.totalCopies}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm font-medium ${
                    availableCount === 0 ? 'text-[var(--lib-danger)]' : availableCount <= 2 ? 'text-[var(--lib-gold-text)]' : 'text-[var(--lib-success)]'
                  }`}>
                    {availableCount}
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                      <button
                        onClick={() => handleEditCopy(book)}
                        className="p-1.5 rounded-lg text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink-muted)] hover:bg-[var(--lib-surface-2)] transition-colors"
                        title="Edit copy"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleAddCopies(book)}
                        className="p-1.5 rounded-lg text-[var(--lib-ink-muted)] hover:text-[var(--lib-gold)] hover:bg-[var(--lib-surface-2)] transition-colors"
                        title="Add copies"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredBooks.length === 0 && (
          <div className="py-12 text-center text-sm text-[var(--lib-ink-muted)]">No copies found</div>
        )}
      </div>

      {/* Update Book Copy Drawer */}
      <UpdateBookCopyForm
        bookCopy={selectedCopy}
        isOpen={isUpdateDialogOpen}
        onClose={() => {
          setIsUpdateDialogOpen(false);
          setSelectedCopy(null);
        }}
        onSuccess={handleUpdateSuccess}
      />

      {/* Add Book Copies Drawer */}
      {selectedBookForAddCopies && (
        <AddBookCopyForm
          bookId={selectedBookForAddCopies.id}
          bookTitle={selectedBookForAddCopies.title}
          open={isAddCopiesDialogOpen}
          onClose={() => {
            setIsAddCopiesDialogOpen(false);
            setSelectedBookForAddCopies(null);
          }}
          onSuccess={handleAddCopiesSuccess}
        />
      )}
    </div>
  );
};

export default InventoryModule;
